import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faPlus, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb, Button, Card, Col, Form, Row } from "@themesberg/react-bootstrap";
import { useHistory, useParams } from "react-router-dom";

import { createProduct, getProductById, updateProduct } from "../api/products.api";
import { useToast } from "../components/ToastProvider";
import { Routes } from "../routes";
import { resolveAssetUrl } from "../utils/assetUrl";

const createEmptyVariant = () => ({
  id: undefined,
  variant_name: "",
  sku: "",
  stock: "",
  low_stock_threshold: "10",
  existing_images: [],
  new_image_files: [],
  image_preview_urls: []
});

const ProductFormSkeleton = () => (
  <>
    <Card border="light" className="shadow-sm">
      <Card.Body>
        <Row>
          <Col xs={12} className="mb-4">
            <div className="skeleton-text" style={{ width: "120px", height: "14px", marginBottom: "8px" }} />
            <div className="skeleton-text" style={{ width: "100%", height: "120px", borderRadius: "0.5rem" }} />
          </Col>
          <Col xs={12} className="mb-3">
            <div className="skeleton-text" style={{ width: "100px", height: "14px", marginBottom: "8px" }} />
            <div className="skeleton-text" style={{ width: "100%", height: "40px" }} />
          </Col>
          <Col xs={12} className="mb-4">
            <div className="skeleton-text" style={{ width: "80px", height: "14px", marginBottom: "8px" }} />
            <div className="skeleton-text" style={{ width: "100%", height: "80px" }} />
          </Col>
        </Row>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="skeleton-text" style={{ width: "80px", height: "18px" }} />
          <div className="skeleton-text" style={{ width: "100px", height: "36px" }} />
        </div>

        {[...Array(3)].map((_, index) => (
          <Card key={index} border="light" className="mb-3">
            <Card.Body>
              <Row>
                <Col xs={12} md={6} className="mb-3">
                  <div className="skeleton-text" style={{ width: "90px", height: "12px", marginBottom: "8px" }} />
                  <div className="skeleton-text" style={{ width: "100%", height: "40px" }} />
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <div className="skeleton-text" style={{ width: "40px", height: "12px", marginBottom: "8px" }} />
                  <div className="skeleton-text" style={{ width: "100%", height: "40px" }} />
                </Col>
                <Col xs={12} md={4} className="mb-3">
                  <div className="skeleton-text" style={{ width: "50px", height: "12px", marginBottom: "8px" }} />
                  <div className="skeleton-text" style={{ width: "100%", height: "40px" }} />
                </Col>
                <Col xs={12} md={4} className="mb-3">
                  <div className="skeleton-text" style={{ width: "120px", height: "12px", marginBottom: "8px" }} />
                  <div className="skeleton-text" style={{ width: "100%", height: "40px" }} />
                </Col>
                <Col xs={12} className="mb-3">
                  <div className="skeleton-text" style={{ width: "90px", height: "12px", marginBottom: "8px" }} />
                  <div className="skeleton-text" style={{ width: "100%", height: "96px" }} />
                </Col>
              </Row>
              <div className="d-flex justify-content-end">
                <div className="skeleton-text" style={{ width: "120px", height: "36px" }} />
              </div>
            </Card.Body>
          </Card>
        ))}

        <div className="d-flex justify-content-end">
          <div className="skeleton-text" style={{ width: "120px", height: "40px", marginRight: "12px" }} />
          <div className="skeleton-text" style={{ width: "120px", height: "40px" }} />
        </div>
      </Card.Body>
    </Card>
  </>
);

export default ({ mode = "create" }) => {
  const history = useHistory();
  const { id } = useParams();
  const { showToast } = useToast();
  const isEditMode = mode === "edit";
  const productInputRef = useRef(null);
  const variantInputRefs = useRef({});
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    existing_images: [],
    product_image_files: [],
    product_image_preview_urls: [],
    variants: [createEmptyVariant()]
  });
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [productDropActive, setProductDropActive] = useState(false);
  const [variantDropActiveIndex, setVariantDropActiveIndex] = useState(null);

  useEffect(() => {
    if (!isEditMode) return;

    const loadProduct = async () => {
      setLoading(true);

      try {
        const product = await getProductById(id);
        setFormData({
          name: product.name || "",
          description: product.description || "",
          existing_images: Array.isArray(product.images) ? product.images.map((image) => image.image_url) : (product.image_url ? [product.image_url] : []),
          product_image_files: [],
          product_image_preview_urls: Array.isArray(product.images) && product.images.length > 0
            ? product.images.map((image) => resolveAssetUrl(image.image_url))
            : (product.image_url ? [resolveAssetUrl(product.image_url)] : []),
          variants: product.variants.length > 0
            ? product.variants.map((variant) => ({
              id: variant.id,
              variant_name: variant.variant_name || "",
              sku: variant.sku || "",
              stock: String(variant.stock ?? ""),
              low_stock_threshold: String(variant.low_stock_threshold ?? 10),
              existing_images: Array.isArray(variant.images) ? variant.images.map((image) => image.image_url) : (variant.image_url ? [variant.image_url] : []),
              new_image_files: [],
              image_preview_urls: Array.isArray(variant.images) && variant.images.length > 0
                ? variant.images.map((image) => resolveAssetUrl(image.image_url))
                : (variant.image_url ? [resolveAssetUrl(variant.image_url)] : [])
            }))
            : [createEmptyVariant()]
        });
      } catch (error) {
        showToast({ message: error.message || "Failed to load product", variant: "danger" });
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, isEditMode, showToast]);

  const updateVariant = (index, field, value) => {
    setFormData((currentData) => ({
      ...currentData,
      variants: currentData.variants.map((variant, variantIndex) => (
        variantIndex === index ? { ...variant, [field]: value } : variant
      ))
    }));
  };

  const appendProductFiles = (files) => {
    if (!files.length) return;

    setFormData((currentData) => ({
      ...currentData,
      product_image_files: [...currentData.product_image_files, ...files],
      product_image_preview_urls: [
        ...currentData.existing_images.map((imageUrl) => resolveAssetUrl(imageUrl)),
        ...currentData.product_image_files.map((file) => URL.createObjectURL(file)),
        ...files.map((file) => URL.createObjectURL(file))
      ]
    }));
  };

  const handleProductImageChange = (event) => {
    appendProductFiles(event.target.files ? Array.from(event.target.files) : []);
  };

  const appendVariantFiles = (index, files) => {
    if (!files.length) return;

    setFormData((currentData) => ({
      ...currentData,
      variants: currentData.variants.map((variant, variantIndex) => (
        variantIndex === index
          ? {
            ...variant,
            new_image_files: [...variant.new_image_files, ...files],
            image_preview_urls: [
              ...variant.existing_images.map((imageUrl) => resolveAssetUrl(imageUrl)),
              ...variant.new_image_files.map((file) => URL.createObjectURL(file)),
              ...files.map((file) => URL.createObjectURL(file))
            ]
          }
          : variant
      ))
    }));
  };

  const handleVariantImageChange = (index, event) => {
    appendVariantFiles(index, event.target.files ? Array.from(event.target.files) : []);
  };

  const removeExistingProductImage = (imageUrlToRemove) => {
    setFormData((currentData) => {
      const existingImages = currentData.existing_images.filter((imageUrl) => imageUrl !== imageUrlToRemove);
      return {
        ...currentData,
        existing_images: existingImages,
        product_image_preview_urls: [
          ...existingImages.map((imageUrl) => resolveAssetUrl(imageUrl)),
          ...currentData.product_image_files.map((file) => URL.createObjectURL(file))
        ]
      };
    });
  };

  const removeProductFile = (fileIndex) => {
    setFormData((currentData) => {
      const newFiles = currentData.product_image_files.filter((_, idx) => idx !== fileIndex);
      const newPreviewUrls = newFiles.map((file) => URL.createObjectURL(file));
      return {
        ...currentData,
        product_image_files: newFiles,
        product_image_preview_urls: [
          ...currentData.existing_images.map((imageUrl) => resolveAssetUrl(imageUrl)),
          ...newPreviewUrls
        ]
      };
    });
  };

  const removeExistingVariantImage = (index, imageUrlToRemove) => {
    setFormData((currentData) => ({
      ...currentData,
      variants: currentData.variants.map((variant, variantIndex) => {
        if (variantIndex !== index) return variant;
        const existingImages = variant.existing_images.filter((imageUrl) => imageUrl !== imageUrlToRemove);
        return {
          ...variant,
          existing_images: existingImages,
          image_preview_urls: [
            ...existingImages.map((imageUrl) => resolveAssetUrl(imageUrl)),
            ...variant.new_image_files.map((file) => URL.createObjectURL(file))
          ]
        };
      })
    }));
  };

  const removeVariantFile = (index, fileIndex) => {
    setFormData((currentData) => ({
      ...currentData,
      variants: currentData.variants.map((variant, variantIndex) => {
        if (variantIndex !== index) return variant;
        const newFiles = variant.new_image_files.filter((_, idx) => idx !== fileIndex);
        const newPreviewUrls = newFiles.map((file) => URL.createObjectURL(file));
        return {
          ...variant,
          new_image_files: newFiles,
          image_preview_urls: [
            ...variant.existing_images.map((imageUrl) => resolveAssetUrl(imageUrl)),
            ...newPreviewUrls
          ]
        };
      })
    }));
  };

  const addVariantRow = () => {
    setFormData((currentData) => ({
      ...currentData,
      variants: [...currentData.variants, createEmptyVariant()]
    }));
  };

  const removeVariantRow = (index) => {
    setFormData((currentData) => ({
      ...currentData,
      variants: currentData.variants.filter((_, variantIndex) => variantIndex !== index)
    }));
  };

  const normalizePayload = () => {
    const payload = new FormData();
    let variantImageUploadIndex = 0;

    const variants = formData.variants
      .filter((variant) => variant.variant_name.trim() && variant.sku.trim())
      .map((variant) => {
        const normalizedVariant = {
          ...(variant.id ? { id: variant.id } : {}),
          variant_name: variant.variant_name.trim(),
          sku: variant.sku.trim(),
          stock: Number(variant.stock || 0),
          low_stock_threshold: Number(variant.low_stock_threshold || 10),
          existing_images: variant.existing_images || []
        };

        if (Array.isArray(variant.new_image_files) && variant.new_image_files.length > 0) {
          normalizedVariant.image_upload_indexes = variant.new_image_files.map(() => variantImageUploadIndex++);
          variant.new_image_files.forEach((file) => {
            payload.append("variant_images", file);
          });
        }

        return normalizedVariant;
      });

    payload.append("name", formData.name.trim());
    payload.append("description", formData.description.trim());
    payload.append("existing_images", JSON.stringify(formData.existing_images || []));
    payload.append("variants", JSON.stringify(variants));

    (formData.product_image_files || []).forEach((file) => {
      payload.append("product_images", file);
    });

    return payload;
  };

const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const payload = normalizePayload();

      if (isEditMode) {
        await updateProduct(id, payload);
      } else {
        await createProduct(payload);
      }

      history.push(Routes.Products.path);
    } catch (error) {
      showToast({ message: error.message || "Failed to save product", variant: "danger" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
            <Breadcrumb.Item onClick={() => history.push(Routes.Products.path)}>Products</Breadcrumb.Item>
            <Breadcrumb.Item active>{isEditMode ? "Edit" : "New"}</Breadcrumb.Item>
          </Breadcrumb>
          <h4>{isEditMode ? "Edit Product" : "Add Product"}</h4>
        </div>
      </div>
      <Card border="light" className="shadow-sm">
        <Card.Body>
          {loading ? (
            <ProductFormSkeleton />
          ) : (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12} className="mb-4">
                  <Form.Group id="productImage">
                    <Form.Label>Product Images</Form.Label>
                    <div
                      onClick={() => productInputRef.current?.click()}
                      onDragEnter={(event) => {
                        event.preventDefault();
                        setProductDropActive(true);
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        setProductDropActive(true);
                      }}
                      onDragLeave={(event) => {
                        event.preventDefault();
                        setProductDropActive(false);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        setProductDropActive(false);
                        appendProductFiles(Array.from(event.dataTransfer.files || []).filter((file) => file.type.startsWith("image/")));
                      }}
                      className={`border rounded p-4 text-center ${productDropActive ? "border-primary bg-light" : "border-muted"}`}
                      style={{ cursor: "pointer", borderStyle: "dashed" }}
                    >
                      <div className="fw-bold mb-1">Drag and drop product images here</div>
                      <div className="text-muted small">or click to browse multiple files</div>
                    </div>
                    <Form.Control
                      ref={productInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleProductImageChange}
                      className="d-none"
                    />
                    <Form.Text className="text-muted">
                      Optional. Upload one or more images for this product.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col xs={12} className="mb-3">
                  <Form.Group id="productName">
                    <Form.Label>Product Name</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      value={formData.name}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setFormData((currentData) => ({ ...currentData, name: nextValue }));
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} className="mb-4">
                  <Form.Group id="productDescription">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.description}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setFormData((currentData) => ({ ...currentData, description: nextValue }));
                      }}
                    />
                  </Form.Group>
                </Col>
                {formData.product_image_preview_urls.length > 0 ? (
                  <Col xs={12} className="mb-4">
                    <div className="border rounded p-3">
                      <div className="small text-muted mb-2">Product Images</div>
                      <div className="d-flex flex-wrap gap-3">
                        {formData.existing_images.map((imageUrl) => (
                          <div key={imageUrl}>
                            <img
                              src={resolveAssetUrl(imageUrl)}
                              alt={formData.name || "Product preview"}
                              style={{ width: "96px", height: "96px", objectFit: "cover", borderRadius: "0.75rem" }}
                            />
                            <Button type="button" size="sm" variant="outline-danger" className="mt-2" onClick={() => removeExistingProductImage(imageUrl)}>
                              Remove
                            </Button>
                          </div>
                        ))}
                        {formData.product_image_files.map((file, fileIndex) => (
                          <div key={`${file.name}-${fileIndex}`}>
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              style={{ width: "96px", height: "96px", objectFit: "cover", borderRadius: "0.75rem" }}
                            />
                            <Button type="button" size="sm" variant="outline-danger" className="mt-2" onClick={() => removeProductFile(fileIndex)}>
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                ) : null}
              </Row>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Variants</h5>
                <Button type="button" variant="outline-primary" onClick={addVariantRow}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Variant
                </Button>
              </div>

              {formData.variants.map((variant, index) => (
                <Card key={variant.id || `new-${index}`} border="light" className="mb-3">
                  <Card.Body>
                    <Row>
                      <Col xs={12} md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>Variant Name</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            value={variant.variant_name}
                            onChange={(event) => updateVariant(index, "variant_name", event.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>SKU</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            value={variant.sku}
                            onChange={(event) => updateVariant(index, "sku", event.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={4} className="mb-3">
                        <Form.Group>
                          <Form.Label>Stock</Form.Label>
                          <Form.Control
                            required
                            type="number"
                            min="0"
                            step="1"
                            value={variant.stock}
                            onChange={(event) => updateVariant(index, "stock", event.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={4} className="mb-3">
                        <Form.Group>
                          <Form.Label>Low Stock Threshold</Form.Label>
                          <Form.Control
                            required
                            type="number"
                            min="0"
                            step="1"
                            value={variant.low_stock_threshold}
                            onChange={(event) => updateVariant(index, "low_stock_threshold", event.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} className="mb-3">
                        <Form.Group>
                          <Form.Label>Variant Images</Form.Label>
                          <div
                            onClick={() => variantInputRefs.current[index]?.click()}
                            onDragEnter={(event) => {
                              event.preventDefault();
                              setVariantDropActiveIndex(index);
                            }}
                            onDragOver={(event) => {
                              event.preventDefault();
                              setVariantDropActiveIndex(index);
                            }}
                            onDragLeave={(event) => {
                              event.preventDefault();
                              setVariantDropActiveIndex((currentIndex) => (currentIndex === index ? null : currentIndex));
                            }}
                            onDrop={(event) => {
                              event.preventDefault();
                              setVariantDropActiveIndex(null);
                              appendVariantFiles(index, Array.from(event.dataTransfer.files || []).filter((file) => file.type.startsWith("image/")));
                            }}
                            className={`border rounded p-3 text-center ${variantDropActiveIndex === index ? "border-primary bg-light" : "border-muted"}`}
                            style={{ cursor: "pointer", borderStyle: "dashed", minHeight: "96px" }}
                          >
                            <div className="fw-bold small">Drop variant images here</div>
                            <div className="text-muted small">or click to browse</div>
                          </div>
                          <Form.Control
                            ref={(node) => {
                              variantInputRefs.current[index] = node;
                            }}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(event) => handleVariantImageChange(index, event)}
                            className="d-none"
                          />
                        </Form.Group>
                        {variant.image_preview_urls.length > 0 ? (
                          <div className="mt-3 d-flex flex-wrap gap-3">
                            {variant.existing_images.map((imageUrl) => (
                              <div key={imageUrl}>
                                <img
                                  src={resolveAssetUrl(imageUrl)}
                                  alt={variant.variant_name || "Variant preview"}
                                  style={{ width: "88px", height: "88px", objectFit: "cover", borderRadius: "0.75rem" }}
                                />
                                <Button type="button" size="sm" variant="outline-danger" className="mt-2" onClick={() => removeExistingVariantImage(index, imageUrl)}>
                                  Remove
                                </Button>
                              </div>
                            ))}
                            {variant.new_image_files.map((file, fileIndex) => (
                              <div key={`${file.name}-${fileIndex}`}>
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  style={{ width: "88px", height: "88px", objectFit: "cover", borderRadius: "0.75rem" }}
                                />
                                <Button type="button" size="sm" variant="outline-danger" className="mt-2" onClick={() => removeVariantFile(index, fileIndex)}>
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end">
                      <Button
                        type="button"
                        variant="outline-danger"
                        onClick={() => removeVariantRow(index)}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                        Remove Variant
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}

              <div className="d-flex justify-content-end">
                <Button type="button" variant="outline-gray-600" className="me-2" onClick={() => history.push(Routes.Products.path)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={submitting}>
                  {submitting ? "Saving..." : isEditMode ? "Update Product" : "Create Product"}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </>
  );
};
